/**
 * OrganiserFayresPage — /organiser/fayres
 *
 * Organiser dashboard: list of their own fayres with ROI counts
 * (supplier_count, share_count, landings), plus a primary CTA to
 * create a new one. Soft-delete via confirm toast. Past events keep
 * displaying so the organiser can see historic performance.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, CalendarBlank, MapPin, Users, Share, Eye, Trash,
  CaretRight, Storefront,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const OrganiserFayresPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [fayres, setFayres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organiser, setOrganiser] = useState(null);

  const load = useCallback(async () => {
    try {
      const [me, list] = await Promise.all([
        axios.get(`${API}/organisers/me`),
        axios.get(`${API}/organisers/me/fayres`),
      ]);
      if (!me.data.organiser) { navigate('/organiser/onboard', { replace: true }); return; }
      setOrganiser(me.data.organiser);
      setFayres(list.data.fayres || []);
    } catch {
      toast.error('Could not load your fayres');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const confirmDelete = (fayre) => {
    toast(`Delete "${fayre.name}"?`, {
      action: {
        label: 'Delete',
        onClick: async () => {
          try {
            await axios.delete(`${API}/organisers/me/fayres/${fayre.id}`);
            toast.success('Fayre removed');
            load();
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not delete');
          }
        },
      },
      duration: 6000,
    });
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white pb-16" data-testid="organiser-fayres-page">
      <div className="sticky top-0 z-10 bg-[#0F1014]/95 backdrop-blur border-b border-white/5 px-5 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/')} className="text-white/60 hover:text-white" data-testid="org-fayres-back-btn">
          <ArrowLeft size={16} weight="bold" />
        </button>
        <div className="flex-1">
          <p className="text-[10px] font-black uppercase tracking-[3px] text-[#C29D52]/80">Organiser</p>
          <p className="font-bold text-sm">{organiser?.display_name || 'Your fayres'}</p>
        </div>
        <button
          onClick={() => navigate('/organiser/fayres/new')}
          className="px-3 py-2 rounded-xl text-[11px] font-black text-white flex items-center gap-1"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid="org-new-fayre-btn"
        >
          <Plus size={12} weight="bold" /> New fayre
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-6">
        {loading ? (
          <p className="text-white/40 text-sm text-center py-16" data-testid="org-fayres-loading">Loading…</p>
        ) : !fayres.length ? (
          <div className="rounded-2xl p-8 bg-white/[0.03] border border-white/10 text-center" data-testid="org-fayres-empty">
            <Storefront size={28} weight="fill" className="mx-auto mb-3 text-white/30" />
            <p className="font-black text-lg">List your first fayre</p>
            <p className="text-white/50 text-sm mt-1">Free. Takes about a minute.</p>
            <button
              onClick={() => navigate('/organiser/fayres/new')}
              className="mt-4 px-4 py-2.5 rounded-xl text-sm font-black text-white inline-flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="org-empty-cta"
            >
              <Plus size={14} weight="bold" /> Create a fayre
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {fayres.map((f) => (
              <div
                key={f.id}
                className={`rounded-xl p-4 border transition-all ${
                  f.is_past
                    ? 'bg-white/[0.015] border-white/5 opacity-75'
                    : 'bg-white/[0.04] border-white/10 hover:border-white/20'
                }`}
                data-testid={`org-fayre-${f.id}`}
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => navigate(`/fayres/${f.id}`)}
                    className="w-14 h-14 rounded-lg shrink-0 flex items-center justify-center text-2xl"
                    style={{ background: 'linear-gradient(135deg, rgba(108,85,224,0.4), rgba(194,157,82,0.25))' }}
                    data-testid={`org-fayre-open-${f.id}`}
                    aria-label="Open fayre"
                  >
                    🎪
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm leading-tight flex items-center gap-1.5">
                      <button onClick={() => navigate(`/fayres/${f.id}`)} className="text-left truncate hover:underline">
                        {f.name}
                      </button>
                      {f.is_past && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-white/[0.06] text-white/50 border border-white/10">
                          PAST
                        </span>
                      )}
                    </p>
                    <p className="text-white/40 text-[11px] mt-1 flex flex-wrap gap-x-2">
                      <span className="inline-flex items-center gap-0.5"><CalendarBlank size={10} weight="bold" /> {formatDate(f.date)}</span>
                      {f.location?.city && <span className="inline-flex items-center gap-0.5"><MapPin size={10} weight="bold" /> {f.location.city}</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => confirmDelete(f)}
                    className="text-white/30 hover:text-rose-400 p-1.5"
                    data-testid={`org-delete-${f.id}`}
                    title="Delete fayre"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
                {/* ROI metrics row */}
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/5 text-[11px]">
                  <MetricChip icon={<Users size={11} weight="fill" className="text-emerald-300" />} label="Suppliers" value={f.supplier_count} testid={`org-metric-suppliers-${f.id}`} />
                  <MetricChip icon={<Share size={11} weight="fill" className="text-[#C29D52]" />} label="Shares" value={f.share_count} testid={`org-metric-shares-${f.id}`} />
                  <MetricChip icon={<Eye size={11} weight="fill" className="text-amber-300" />} label="Visits" value={f.landing_count} testid={`org-metric-landings-${f.id}`} />
                  <div className="flex-1" />
                  <button
                    onClick={() => navigate(`/fayres/${f.id}`)}
                    className="text-[11px] text-[#C29D52] font-black hover:underline flex items-center gap-1"
                    data-testid={`org-view-${f.id}`}
                  >
                    View <CaretRight size={10} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MetricChip = ({ icon, label, value, testid }) => (
  <span className="inline-flex items-center gap-1 text-white/70" data-testid={testid}>
    {icon}
    <span className="font-bold">{value ?? 0}</span>
    <span className="text-white/40 hidden sm:inline">{label}</span>
  </span>
);

export default OrganiserFayresPage;
